import React, { useEffect, useState } from 'react'

import { useTranslation } from 'language/translator'

import { IEntity, IEntityColumn } from 'types/GlobalEntities'
import { IUnderlyingDataResponse } from 'types/GlobalWidget'
import { TDataDialogColumn } from '../types'

import { useEntityData, useEntityFetchStatus } from 'redux/hooks/Entities'

import { Popover } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import { AiIconTip } from 'components_new/molecules/IconTip'
import CheckboxItem from 'components_new/molecules/CheckboxItem'
import DndList from 'components_new/molecules/DndList'
import DndListItem from 'components_new/molecules/DndList/DndListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'

interface Props {
  columns: { [columnId: string]: TDataDialogColumn }
  entityId: string | null
  onSubmit: (columns: TDataDialogColumn[]) => void
  rawData: IUnderlyingDataResponse | null
}

const ColumnsButton = (props: Props) => {
  const { columns, entityId, onSubmit, rawData } = props
  const [entities] = useEntityData()
  const fetchStatus = useEntityFetchStatus()

  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [entity, setEntity] = useState<IEntity | null>(null)
  const [tempElements, setTempElements] = useState<IEntityColumn[]>([])

  useEffect(() => {
    const currentEntity = entityId ? entities[entityId] : undefined

    if (fetchStatus === 'success' && currentEntity) {
      const currentSelectedEntityColumns = currentEntity.columns
        .map((item) => {
          const selectedColumn = columns[item.id]

          // use default index for -1
          const selectedIndex = selectedColumn?.index ?? -1

          return {
            ...item,
            index: selectedIndex === -1 ? item.index : selectedIndex,
            hidden: selectedColumn?.hidden ?? item.hidden
          }
        })
        .sort((a, b) => a.index - b.index)

      setEntity(currentEntity)
      setTempElements(currentSelectedEntityColumns)
    }
  }, [fetchStatus, entityId])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSubmit = () => {
    onSubmit(
      tempElements.map((item) => ({
        id: item.id,
        index: item.index,
        hidden: item.hidden
      }))
    )

    handleClose()
  }

  const handleToggle = (id: string) => {
    setTempElements((prevElements) =>
      prevElements.map((column) =>
        column.id === id ? { ...column, hidden: !column.hidden } : column
      )
    )
  }

  return (
    <>
      <TooltipWrapper title={t('global', 'edit_columns')}>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
          <Icon fontSize="small" name="ViewColumn" />
        </IconButton>
      </TooltipWrapper>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 540,
              maxWidth: 300,
              p: 2
            }
          }
        }}
      >
        <SectionHeader
          actions={
            <Button disabled={true} size="small" variant="text">
              {t('global', 'reset')}
            </Button>
          }
          size="medium"
          title={t('global', 'columns')}
        />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {entity?.segment_is_hidden ? null : (
            <DndListItem
              disabled={true}
              editable={true}
              sx={{ mb: 0.5 }}
              variant="outlined"
            >
              <CheckboxItem
                checked={true}
                label={entity?.segment}
                sx={{ mr: 1 }}
              />
              <Chip label={t('global', 'identifier')} size="small" />
            </DndListItem>
          )}

          {Boolean(rawData?.data[0]?.kpi_option_id) ? (
            <DndListItem
              disabled={true}
              editable={true}
              sx={{ mb: 0.5 }}
              variant="outlined"
            >
              <CheckboxItem
                checked={true}
                label={rawData?.data[0].label}
                sx={{ mr: 1 }}
              />
              <Chip label={t('metrics', 'metric')} size="small" />
            </DndListItem>
          ) : null}

          <DndList
            editable={true}
            handleDragEnd={(newOrder) => {
              setTempElements((prevState) => {
                return newOrder.map((id, index) => {
                  const element = prevState.find(
                    (item) => item.id === id
                  ) as IEntityColumn

                  return {
                    ...element,
                    index
                  }
                })
              })
            }}
            itemGap={4}
            itemHeight={40}
            items={tempElements.map((item) => {
              return {
                id: item.id,
                content: (
                  <>
                    <CheckboxItem
                      checked={!item.hidden}
                      label={item.name}
                      onChange={() => handleToggle(item.id)}
                    />
                    {item.ai_generated ? (
                      <AiIconTip
                        sx={{ ml: 0.5 }}
                        title={t('global', 'ai_generated')}
                      />
                    ) : null}
                  </>
                )
              }
            })}
          />
        </Box>

        <Box sx={{ flexShrink: 0, pt: 2 }}>
          <Button fullWidth={true} onClick={handleSubmit}>
            {t('global', 'save')}
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default ColumnsButton
